import React, { Component } from 'react';

import {
  allQuestionsAnswered,
  buildConfigurationPayload,
  findSelectedOption,
  getEstimatedPriceCents,
  getMaxUnlockedWizardStepIndex,
  optionPriceLabel,
  questionIndex
} from '~/components/legacy/projectLicenseWizard.js';

class CheckoutLicenseWizard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      answers: props.initialAnswers || {},
      wizardStepIndex: typeof props.initialWizardStepIndex === 'number' ? props.initialWizardStepIndex : 0
    };
  }

  componentDidMount() {
    this.emitWizardChange();

    if (allQuestionsAnswered(this.props.family, this.state.answers) && typeof this.props.onWizardReady === 'function') {
      this.props.onWizardReady({
        answers: this.state.answers,
        wizardStepIndex: this.currentWizardStepIndex()
      });
    }
  }

  componentDidUpdate(_prevProps, prevState) {
    if (
      JSON.stringify(prevState.answers) !== JSON.stringify(this.state.answers) ||
      prevState.wizardStepIndex !== this.state.wizardStepIndex
    ) {
      this.emitWizardChange();
    }
  }

  i18nValue(key) {
    if (this.props.i18n && this.props.i18n[key] !== undefined) {
      return this.props.i18n[key];
    }

    return CheckoutLicenseWizard.defaultProps.i18n[key];
  }

  answers() {
    return this.state.answers;
  }

  maxUnlockedWizardStepIndex(answers = this.answers()) {
    return getMaxUnlockedWizardStepIndex(this.props.family, answers, { skipWizardResultStep: true });
  }

  currentWizardStepIndex() {
    return Math.min(this.state.wizardStepIndex, this.maxUnlockedWizardStepIndex());
  }

  currentQuestion() {
    return this.props.family.questions[this.currentWizardStepIndex()] || null;
  }

  familyLabel() {
    const familyTitle = (this.props.family?.title || '').trim();

    if (!familyTitle) {
      return '';
    }

    if (/\s+lizenz$/i.test(familyTitle)) {
      return familyTitle.replace(/\s+lizenz$/i, '-Lizenz');
    }

    return /-lizenz$/i.test(familyTitle) ? familyTitle : `${familyTitle}-Lizenz`;
  }

  progressCopy() {
    const familyLabel = this.familyLabel();
    const stepCopy = `${this.currentWizardStepIndex() + 1} / ${this.props.family.questions.length}`;

    if (!familyLabel) {
      return stepCopy;
    }

    return `Konfiguration Ihrer ${familyLabel}: Frage ${stepCopy}`;
  }

  emitWizardChange() {
    if (typeof this.props.onWizardChange !== 'function') {
      return;
    }

    this.props.onWizardChange({
      answers: this.state.answers,
      wizardStepIndex: this.currentWizardStepIndex(),
      estimatedPriceCents: getEstimatedPriceCents(this.props.family, this.state.answers),
      selectedOptionKeys: buildConfigurationPayload(this.props.family, this.state.answers).selectedOptionKeys
    });
  }

  handleOptionSelect(questionKey, optionKey) {
    const nextAnswers = {
      ...this.answers(),
      [questionKey]: optionKey
    };
    const currentQuestionIndex = questionIndex(this.props.family, questionKey);
    const allAnswered = allQuestionsAnswered(this.props.family, nextAnswers);
    const lastQuestionIndex = Math.max(this.props.family.questions.length - 1, 0);
    const shouldSubmit = allAnswered && currentQuestionIndex >= lastQuestionIndex;
    const nextWizardStepIndex = shouldSubmit ?
      currentQuestionIndex :
      Math.min(currentQuestionIndex + 1, this.maxUnlockedWizardStepIndex(nextAnswers));

    this.setState({
      answers: nextAnswers,
      wizardStepIndex: nextWizardStepIndex
    }, () => {
      if (shouldSubmit && typeof this.props.onSubmit === 'function') {
        this.props.onSubmit(buildConfigurationPayload(this.props.family, nextAnswers));
      }
    });
  }

  goToWizardStep(nextIndex) {
    const clampedStepIndex = Math.max(0, Math.min(nextIndex, this.maxUnlockedWizardStepIndex()));

    this.setState({ wizardStepIndex: clampedStepIndex });
  }

  renderQuestion(question, index) {
    const selectedOption = findSelectedOption(question, this.answers()[question.key]);

    return (
      <div className='checkout-license-wizard__question sub-panel sub-panel--success' key={question.key}>
        <div className='checkout-license-wizard__question-meta'>
          <span className='checkout-license-wizard__question-step'>{index + 1}</span>
          <div>
            <div className='checkout-license-wizard__question-title'>{question.title}</div>
            <div className={`checkout-license-wizard__question-selection ${selectedOption ? '' : 'checkout-license-wizard__question-selection--muted'}`}>
              {selectedOption ? selectedOption.summary : this.i18nValue('choose_option')}
            </div>
          </div>
        </div>
        <div className='checkout-license-wizard__option-row'>
          {question.options.map((option) => {
            const selected = this.answers()[question.key] === option.key;

            return (
              <button
                type='button'
                className={selected ? 'checkout-license-wizard__option checkout-license-wizard__option--selected' : 'checkout-license-wizard__option'}
                key={option.key}
                onClick={() => this.handleOptionSelect(question.key, option.key)}
              >
                <span className='checkout-license-wizard__option-label'>{option.label}</span>
                {optionPriceLabel(this.props.family, question, option, this.answers(), this.props.locale) ? (
                  <span className='checkout-license-wizard__option-price'>{optionPriceLabel(this.props.family, question, option, this.answers(), this.props.locale)}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  render() {
    const currentQuestion = this.currentQuestion();
    const currentStepIndex = this.currentWizardStepIndex();

    if (!currentQuestion) {
      return null;
    }

    return (
      <div className='checkout-license-wizard'>
        <div className='checkout-license-wizard__progress'>
          <span className='checkout-license-wizard__progress-copy'>
            {this.progressCopy()}
          </span>
          <div className='checkout-license-wizard__progress-track'>
            <div
              className='checkout-license-wizard__progress-fill'
              style={{ width: `${((currentStepIndex + 1) / this.props.family.questions.length) * 100}%` }}
            ></div>
          </div>
        </div>
        {this.renderQuestion(currentQuestion, currentStepIndex)}
        {currentStepIndex > 0 ? (
          <div className='checkout-license-wizard__actions'>
            <button
              type='button'
              className='checkout-license-wizard__back-link'
              onClick={() => this.goToWizardStep(currentStepIndex - 1)}
            >
              {this.i18nValue('back')}
            </button>
          </div>
        ) : null}
      </div>
    );
  }
}

CheckoutLicenseWizard.defaultProps = {
  locale: 'de',
  family: null,
  initialAnswers: {},
  initialWizardStepIndex: 0,
  onSubmit: null,
  onWizardReady: null,
  onWizardChange: null,
  i18n: {
    choose_option: 'Bitte wählen Sie eine Option.',
    back: 'Zurück'
  }
};

export default CheckoutLicenseWizard;
